import { createSlice } from '@reduxjs/toolkit';
import AppError from '../../models/error';
import { UserType } from '../../enums/user-type';
import { Status } from '../../enums/status';
import { setApiSuccess, setApiError, setApiLoading, ApiStatusKeys } from './apiStatusSlice';
import axios from '../../config/axios-config';
import TokenService from '../../services/tokenService';
import { handleApiError, handleError } from '../../helpers/errorHelper';

const initialState = {
  newUser: {
    status: Status.Enabled,
    userType: UserType.Member,
    isDeleted: false,
  },
  currentUser: null,
  loading: false,
  error: null,
};

// Yardımcı fonksiyonlar
const isValidEmail = (email) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setFormError: (state, action) => {
      state.error = action.payload instanceof AppError ? action.payload : AppError.fromJson(action.payload);
    },
    clearFormError: (state) => {
      state.error = null;
    },
    setNewUserEmail: (state, action) => {
      const email = action.payload;

      // Email validasyonları
      if (!email?.trim()) {
        state.error = AppError.validationError('signup.error-empty-email');
        return;
      }

      if (!isValidEmail(email)) {
        state.error = AppError.validationError('signup.error-invalid-email');
        return;
      }

      state.newUser = {
        ...state.newUser,
        email: email,
      };
      state.error = null;
    },
    updateNewUser: (state, action) => {
      state.newUser = {
        ...state.newUser,
        ...action.payload,
      };
    },
    setCurrentUser: (state, action) => {
      state.currentUser = action.payload;
      state.error = null;
    },
    updateCurrentUser: (state, action) => {
      state.currentUser = {
        ...state.currentUser,
        ...action.payload,
      };
      state.error = null;
    },
    clearCurrentUser: (state) => {
      state.currentUser = null;
      state.error = null;
    },
    clearNewUser: (state) => {
      state.newUser = null;
      state.error = null;
      localStorage.setItem('newUser', null);
      TokenService.removeToken();
    },

    resetUser: (state) => {
      state.newUser = {
        status: Status.Enabled,
        userType: UserType.Member,
        isDeleted: false,
      };
      state.error = null;
    },
    setImageFileName: (state, action) => {
      if (state.newUser != null) {
        state.newUser.imageFileName = action.payload;
      }

      if (state.currentUser != null) {
        state.currentUser.imageFileName = action.payload;
      }
    },
    setImageUrl: (state, action) => {
      if (state.newUser != null) {
        state.newUser.imageUrl = action.payload;
      }

      if (state.currentUser != null) {
        state.currentUser.imageUrl = action.payload;
      }
    },
  },
});

export const {
  setFormError,
  clearFormError,
  setNewUserEmail,
  updateNewUser,
  setCurrentUser,
  updateCurrentUser,
  clearCurrentUser,
  resetUser,
  setImageFileName,
  setImageUrl,
  clearNewUser,
} = userSlice.actions;

export const uploadImageAsTemporary = (file) => async (dispatch) => {
  try {
    dispatch(setApiLoading(ApiStatusKeys.UPLOAD_IMAGE));
    // Dosyayı base64'e çevir
    const reader = new FileReader();
    const base64String = await new Promise((resolve, reject) => {
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

    const response = await axios({
      method: 'post',
      url: '/auth/upload-profile-picture',
      data: {
        file: base64String,
      },
    });

    if (response.data.success) {
      dispatch(setImageUrl(response.data.imageUrl));
      dispatch(setImageFileName(response.data.fileName));

      dispatch(setApiSuccess(ApiStatusKeys.UPLOAD_IMAGE));
      return {
        imageUrl: response.data.imageUrl,
        imageFileName: response.data.fileName,
      };
    } else {
      throw new Error(response.data.errorMessage);
    }
  } catch (error) {
    handleApiError(error, ApiStatusKeys.UPLOAD_IMAGE, dispatch, 'errors.general');
    throw error;
  }
};

export const refineCoachInformation = (information, coachInformationType) => async (dispatch, getState) => {
  try {
    dispatch(setApiLoading({ key: ApiStatusKeys.REFINE_COACH_INFO, loading: true }));

    const state = getState();

    const updateRequest = {
      CoachInformationType: coachInformationType,
      Information: information,
      Language: state.language.lang,
    };

    const response = await axios({
      method: 'post',
      url: '/ai/refine',
      data: updateRequest,
    });

    if (response.data.success) {
      dispatch(setApiSuccess(ApiStatusKeys.REFINE_COACH_INFO));
      return response.data.information;
    } else {
      throw new Error(response.data.errorMessage);
    }
  } catch (error) {
    const errorMessage = handleApiError(error, ApiStatusKeys.REFINE_COACH_INFO, dispatch, 'errors.refine_failed');
    throw new Error(errorMessage);
  } finally {
    dispatch(setApiLoading({ key: ApiStatusKeys.REFINE_COACH_INFO, loading: false }));
  }
};

export const updateUserProfile = (formData) => async (dispatch, getState) => {
  try {
    dispatch(setApiLoading(ApiStatusKeys.UPDATE_USER_PROFILE));

    const state = getState();
    const currentUser = state.user.currentUser;

    // API formatına uygun veri hazırlama
    const profileData = {
      UserId: currentUser.id,
      Email: formData.email,
      ImageFileName: currentUser.imageFileName,
      City: formData.city,
      Country: formData.country,
      Phone: formData.phone,
    };

    const response = await axios.post('/user/update-user-profile', profileData);

    if (response.data.success) {
      dispatch(updateCurrentUser(response.data.user));
      dispatch(setApiSuccess(ApiStatusKeys.UPDATE_USER_PROFILE));
    } else {
      throw new Error(response.data.errorMessage);
    }
  } catch (error) {
    const errorMessage = handleApiError(
      error,
      ApiStatusKeys.UPDATE_USER_PROFILE,
      dispatch,
      'errors.profile_update_failed',
    );
    throw new Error(errorMessage);
  }
};

export const updateCoachingProfile = (formData, selectedCategories) => async (dispatch, getState) => {
  try {
    dispatch(setApiLoading(ApiStatusKeys.UPDATE_COACHING_PROFILE));

    const state = getState();
    const currentUser = state.user.currentUser;

    if (!selectedCategories || selectedCategories.length === 0) {
      throw new Error('signup.error.empty-categories');
    }

    // API formatına uygun veri hazırlama
    const coachingData = {
      UserId: currentUser.id,
      Headline: formData.headline,
      Bio: formData.bio,
      LinkedIn: formData.linkedIn,
      Website: formData.website,
      Instagram: formData.instagram,
      Facebook: formData.facebook,
      Categories: selectedCategories,
    };

    const response = await axios.post('/user/update-coaching-profile', coachingData);

    if (response.data.success) {
      dispatch(updateCurrentUser(response.data.user));
      dispatch(setApiSuccess(ApiStatusKeys.UPDATE_COACHING_PROFILE));
    } else {
      throw new Error(response.data.errorMessage);
    }
  } catch (error) {
    const errorMessage = handleApiError(
      error,
      ApiStatusKeys.UPDATE_COACHING_PROFILE,
      dispatch,
      'errors.profile_update_failed',
    );
    throw new Error(errorMessage);
  }
};

export const updateCoachingCertificates = (certificates) => async (dispatch, getState) => {
  try {
    dispatch(setApiLoading(ApiStatusKeys.UPDATE_COACHING_CERTIFICATES));

    const state = getState();
    const currentUser = state.user.currentUser;

    if (!certificates || certificates.length === 0) {
      throw new Error('signup.error.certificates-validation-failed');
    }

    // API formatına uygun veri hazırlama
    const certificateData = {
      UserId: currentUser.id,
      Certificates: certificates,
    };

    const response = await axios.post('/user/update-coaching-certificate', certificateData);

    if (response.data.success) {
      dispatch(updateCurrentUser(response.data.user));
      dispatch(setApiSuccess(ApiStatusKeys.UPDATE_COACHING_CERTIFICATES));
    } else {
      throw new Error(response.data.errorMessage);
    }
  } catch (error) {
    const errorMessage = handleApiError(
      error,
      ApiStatusKeys.UPDATE_COACHING_CERTIFICATES,
      dispatch,
      'errors.profile_update_failed',
    );
    throw new Error(errorMessage);
  }
};

export default userSlice.reducer;
